import type { Database } from "../../../__definitions__/database-definitions";
import { useSupabaseLoader } from "../../../server/supabase/hooks";
import type { UseSupabaseLoaderState } from "../../../server/supabase/hooks/types";
import type { UUID } from "../../../utils/uuid";
import type { ChannelName } from "../../channels/types";
import { SupabaseAccountService } from "../../generic/server/supabase-account-service";
import { SupabaseMergeTagService } from "../../merge-tag/server/supabase-merge-tag-service";
import type { Tags } from "../../merge-tag/types";
import type { GetPersonsInSegmentJsonPerson } from "../../segment/components/manager-v2/types";
import { SupabaseSlackAppService } from "../../slack/server/slack-supabase-service";
import { SupabaseFollowUpMomentDetailsService } from "../server/follow-up-service";

export type FollowUpMomentDetails = Database["public"]["Views"]["follow_up_moment_details"]["Row"];

export type FollowUpSegmentPerson = Omit<GetPersonsInSegmentJsonPerson, "channels"> & {
  channels: Partial<Record<NonNullable<ChannelName>, string>>;
};

export type FollowUpMomentDetailsLoaderData = {
  momentFollowUpDetails: FollowUpMomentDetails;
  enabledChannels: NonNullable<ChannelName>[];
  segmentPersons?: FollowUpSegmentPerson[];
  mergeTags: Tags;
  slackVideoEnabled: boolean;
};

export function useFollowUpMomentDetails(
  moment_id: UUID,
): UseSupabaseLoaderState<FollowUpMomentDetailsLoaderData> {
  return useSupabaseLoader(
    async ({ supabase, account_id }) => {
      const [
        { data: momentFollowUpDetails, error: momentFollowUpDetailsError },
        enabledChannels,
        mergeTags,
        slackVideoEnabled,
      ] = await Promise.all([
        new SupabaseFollowUpMomentDetailsService(supabase).get(moment_id),
        new SupabaseAccountService(supabase).getEnabledChannels(account_id),
        new SupabaseMergeTagService(supabase).getAllMergeTags(),
        new SupabaseSlackAppService(supabase).videoEnabled(account_id),
      ]);

      if (momentFollowUpDetailsError) throw momentFollowUpDetailsError;

      if (momentFollowUpDetails.segment?.segment_json) {
        const { data: persons, error: err } = await supabase.rpc("get_persons_in_segment_json", {
          account_id_: account_id,
          segment_json: momentFollowUpDetails.segment.segment_json,
          target_manager_: momentFollowUpDetails.target_manager ?? undefined,
          target_connection_type_: momentFollowUpDetails.target_connection_type ?? undefined,
        });

        if (err) throw err;

        return {
          momentFollowUpDetails,
          enabledChannels,
          segmentPersons: persons.map((p) => ({
            ...p,
            channels: p.channels as Partial<Record<NonNullable<ChannelName>, string>>,
          })),
          mergeTags,
          slackVideoEnabled,
        };
      }

      return {
        momentFollowUpDetails,
        enabledChannels,
        mergeTags,
        slackVideoEnabled,
      };
    },
    [moment_id],
  );
}
